import { useEffect, useState } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Loader2, CreditCard, Plane, Bus, Hotel, Car, Mountain } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { inr } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import Shell from "@/components/Shell";

const ICONS = { flight: Plane, bus: Bus, hotel: Hotel, cab: Car, experience: Mountain };

export default function Checkout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const state = location.state;

  const [form, setForm] = useState({ customer_name: "", customer_email: "", customer_phone: "" });
  const [providers, setProviders] = useState(null);
  const [provider, setProvider] = useState("stripe");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/payments/providers").then((r) => setProviders(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (user) setForm((f) => ({ ...f, customer_name: f.customer_name || user.name, customer_email: f.customer_email || user.email }));
  }, [user]);

  if (!state || !state.type) return <Navigate to="/" replace />;

  const Icon = ICONS[state.icon || state.type] || Mountain;

  const pay = async () => {
    if (!form.customer_name.trim() || !/\S+@\S+\.\S+/.test(form.customer_email)) {
      toast.error("Please enter your name and a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      const bookingRes = await api.post("/bookings", {
        type: state.type, item_id: state.item_id,
        customer_name: form.customer_name, customer_email: form.customer_email, customer_phone: form.customer_phone,
        details: state.details,
      });
      const booking = bookingRes.data;
      if (provider === "stripe") {
        const co = await api.post("/payments/checkout", { booking_id: booking.id, origin_url: window.location.origin });
        window.location.href = co.data.checkout_url;
        return;
      }
      if (provider === "razorpay") await api.post("/payments/razorpay/order", { booking_id: booking.id });
      else if (provider === "paypal") await api.post("/payments/paypal/order", { booking_id: booking.id });
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Shell footer={false}>
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10 grid md:grid-cols-12 gap-8">
        {/* Left: details + payment */}
        <div className="md:col-span-7">
          <h1 className="font-display text-4xl text-foreground">Checkout</h1>
          <p className="text-muted-foreground mt-1 flex items-center gap-2 text-sm"><Lock size={14} /> Secure checkout · you review before paying</p>

          <div className="mt-8">
            <h2 className="font-semibold text-foreground mb-4">Lead traveller</h2>
            <div className="space-y-4">
              <input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Full name" data-testid="co-name"
                className="w-full h-12 rounded-xl border border-input bg-transparent px-4 focus:ring-2 focus:ring-accent focus:outline-none" />
              <input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} placeholder="Email" data-testid="co-email"
                className="w-full h-12 rounded-xl border border-input bg-transparent px-4 focus:ring-2 focus:ring-accent focus:outline-none" />
              <input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} placeholder="Phone (optional)" data-testid="co-phone"
                className="w-full h-12 rounded-xl border border-input bg-transparent px-4 focus:ring-2 focus:ring-accent focus:outline-none" />
            </div>
          </div>

          <div className="mt-8">
            <h2 className="font-semibold text-foreground mb-4">Payment method</h2>
            <div className="space-y-3" data-testid="checkout-providers">
              {providers && Object.entries(providers).map(([key, val]) => (
                <button key={key} onClick={() => val.enabled && setProvider(key)} disabled={!val.enabled} data-testid={`co-provider-${key}`}
                  className={`w-full flex items-center justify-between rounded-xl border p-4 text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${provider === key && val.enabled ? "border-accent bg-accent/5" : "border-primary/15 hover:bg-secondary"}`}>
                  <span className="flex items-center gap-3">
                    <span className={`grid place-items-center w-9 h-9 rounded-lg ${provider === key && val.enabled ? "bg-accent text-accent-foreground" : "bg-secondary text-foreground"}`}><CreditCard size={18} /></span>
                    <span className="font-medium text-foreground">{val.label}</span>
                  </span>
                  {val.enabled ? <span className={`w-5 h-5 rounded-full border-2 ${provider === key ? "border-accent bg-accent" : "border-primary/30"}`} /> : <span className="text-xs text-muted-foreground">Add keys to enable</span>}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-3">Test card: 4242 4242 4242 4242 · any future expiry · any CVC</p>
          </div>

          <button onClick={pay} disabled={submitting} data-testid="checkout-pay"
            className="w-full mt-8 rounded-full bg-accent text-accent-foreground h-14 font-semibold flex items-center justify-center gap-2 disabled:opacity-60 hover:-translate-y-px transition-transform">
            {submitting ? <Loader2 size={18} className="animate-spin" /> : <Lock size={18} />} Pay {inr(state.amount)}
          </button>
        </div>

        {/* Right: summary */}
        <div className="md:col-span-5">
          <div className="rounded-2xl border border-primary/10 bg-card overflow-hidden sticky top-24" data-testid="checkout-summary">
            {state.image ? (
              <img src={state.image} alt={state.title} className="w-full h-40 object-cover" />
            ) : (
              <div className="h-32 bg-primary grid place-items-center text-primary-foreground"><Icon size={40} /></div>
            )}
            <div className="p-6">
              <span className="text-xs font-semibold uppercase tracking-wider text-accent">{state.type}</span>
              <h3 className="font-semibold text-lg text-foreground mt-1">{state.title}</h3>
              <div className="mt-4 space-y-2 text-sm">
                {(state.summary || []).map((s) => (
                  <div key={s.k} className="flex items-center justify-between">
                    <span className="text-muted-foreground">{s.k}</span>
                    <span className="text-foreground font-medium text-right">{s.v}</span>
                  </div>
                ))}
              </div>
              <div className="mt-5 pt-5 border-t border-primary/10 flex items-center justify-between">
                <span className="text-muted-foreground">Total</span>
                <span className="font-display text-3xl text-foreground">{inr(state.amount)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Shell>
  );
}
